import { Column, type ColumnBodyOptions, type ColumnEditorOptions } from "primereact/column";
import {
  DataTable,
  type DataTablePageEvent,
  type DataTableRowEditCompleteEvent,
  type DataTableSortMeta,
  type DataTableValue,
} from "primereact/datatable";
import { type CSSProperties, type ReactNode, useEffect, useRef, useState } from "react";
import SlidersHIcon from "../../../../components/icons/SlidersHIcon";
import NoDataMessage from "../NoDataMessage/NoDataMessage";

import { useWrapAsync } from "@paroicms/front-app-log";
import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import type { Nullable } from "primereact/ts-helpers";
import {
  type Pagination,
  type PaginationOptions,
  apiRequestWrapper,
} from "../../../../api/shared/http-client";
import PlusIcon from "../../../../components/icons/PlusIcon";
import Icon from "../Icons/Icon";
import SearchInput from "../SearchInput/SearchInput";
import Spinner from "../Spinner/Spinner";
import "./OurDataTable.scss";

export interface OurDataTableProps<T> {
  className?: string;
  columns: ColumnType<T>[];
  headerGroup?: JSX.Element;
  data: DataTableValue[];
  options?: DataTableOptions;
  loadData?(options: PaginationOptions): Promise<Pagination>;
  /**
   * When `data` already reflects the current page (e.g. it was fetched
   * alongside other data by the caller), pass the matching total here so
   * the table renders it immediately instead of discarding it in favor of
   * a redundant `loadData` call on mount. `loadData` is still used for
   * subsequent page changes.
   */
  initialTotal?: number;
}

export interface DataTableOptions {
  /**
   * Default value true
   */
  scrollable?: boolean;
  stripedRows?: boolean;
  /**
   * Default value 6
   */
  rows?: number;
  filterByPeriod?: boolean;
  onSearch?(value: string, options: PaginationOptions): Promise<Pagination>;
  tableStyle?: CSSProperties;
  footer?: boolean;
  headerTitle?: string;
  headerBtns?: {
    addBtn: {
      label: string;
      onClick(): void;
    };
    uploadBtn?: {
      label: string;
      onClick(): void;
    };
  };
  editMode?: "row" | "cell";
  onRowEditComplete?(event: DataTableRowEditCompleteEvent): void;
}

export interface ColumnType<T> {
  /**
   * If not set, property headerGroup should be provided
   */
  field?: string;
  /**
   * If not set, property headerGroup should be provided
   */
  header?: string;
  style?: CSSProperties;
  body?: (datum: T, options: ColumnBodyOptions) => ReactNode;
  key?: string;
  editor?: ReactNode | ((options: ColumnEditorOptions) => React.ReactNode);
  rowEditor?: boolean | ((data: any, options: ColumnBodyOptions) => boolean) | undefined;
  headerStyle?: CSSProperties;
  bodyStyle?: CSSProperties;
  colSpan?: number;
}

interface LazyTableState {
  first: number;
  rows: number;
  page: number;
  multiSortMeta?: DataTableSortMeta[];
}

export default function OurDataTable<T>({
  className,
  columns,
  headerGroup,
  data,
  loadData,
  initialTotal,
  options = {},
}: OurDataTableProps<T>) {
  const {
    rows = 6,
    filterByPeriod,
    scrollable = true,
    onSearch,
    stripedRows,
    tableStyle,
    footer,
    headerBtns,
    headerTitle,
    editMode,
    onRowEditComplete,
  } = options;
  const wrapAsync = useWrapAsync();
  const [globalSearchFilterValue, setGlobalSearchFilterValue] = useState("");
  const [dates, setDates] = useState<Nullable<(Date | null)[]>>(null);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState<number | undefined>(initialTotal);
  const [lazyState, setLazyState] = useState<LazyTableState>({
    first: 0,
    rows,
    page: 1,
  });
  const skipNextLoad = useRef(initialTotal !== undefined);

  const onPage = ({ first, rows, page, multiSortMeta }: DataTablePageEvent) => {
    setLazyState({
      first,
      rows,
      page: page ? page + 1 : 1,
      multiSortMeta: multiSortMeta ?? [],
    });
  };
  const handleSearch = wrapAsync(async (val: string, page?: number) => {
    if (!onSearch) return;
    setLoading(true);
    await apiRequestWrapper(async () => {
      const result = await onSearch(val, {
        limit: rows,
        page: page ?? 1,
      });
      setTotal(result.total);
    });
    setLoading(false);
  });
  const fetchData = async (page?: number) => {
    if (!loadData) return;
    setLoading(true);
    await apiRequestWrapper(async () => {
      const result = await loadData({
        limit: rows,
        page: page ?? lazyState.page,
      });
      setTotal(result.total);
    });
    setLoading(false);
  };
  const loadLazyData = wrapAsync(async (page?: number) => {
    if (globalSearchFilterValue !== "") {
      handleSearch(globalSearchFilterValue, lazyState.page);
      return;
    }
    await fetchData(page);
  });

  useEffect(() => {
    if (skipNextLoad.current) {
      skipNextLoad.current = false;
      return;
    }
    loadLazyData();
  }, [lazyState]);

  useEffect(() => {
    if (initialTotal === undefined) return;
    skipNextLoad.current = true;
    setTotal(initialTotal);
    setLazyState({ first: 0, rows, page: 1 });
  }, [initialTotal]);

  const renderHeader = () => {
    return (
      <div className="OurDataTable-header">
        {headerTitle && (
          <div className="OurDataTable-headerTitle flex justify-content-between">
            <h3 className="OurDataTable-title">{headerTitle}</h3>
            <SlidersHIcon classNames="secondary" />
          </div>
        )}

        <div className={`OurDataTable-headercontent${headerBtns ? " hasBtns" : ""}`}>
          {headerBtns && (
            <div className="OurDataTable-headerBtns">
              <Button
                className="Btn sm primary icon"
                onClick={headerBtns.addBtn.onClick}
                label={headerBtns.addBtn.label}
                icon={<PlusIcon />}
              />
              {headerBtns.uploadBtn && (
                <Button
                  className="Btn sm success icon"
                  onClick={headerBtns.uploadBtn.onClick}
                  label={headerBtns.uploadBtn.label}
                  icon="pi pi-save"
                />
              )}
            </div>
          )}

          {(filterByPeriod || onSearch) && (
            <div
              className={`OurDataTable-headerInputs${filterByPeriod && !headerBtns ? " w-full" : ""}`}
            >
              {filterByPeriod && (
                <Calendar
                  value={dates}
                  onChange={(e) => setDates(e.value)}
                  selectionMode="range"
                  readOnlyInput
                  hideOnRangeSelection
                  className="OurDataTable-filterByPeriod"
                  showIcon
                  placeholder="Filtre par Date"
                />
              )}

              {onSearch && (
                <SearchInput
                  size="sm"
                  value={globalSearchFilterValue}
                  onChange={(value) => setGlobalSearchFilterValue(value)}
                  onSearch={(val) => {
                    handleSearch(val);
                    setLazyState({
                      first: 0,
                      rows,
                      page: 1,
                    });
                  }}
                  onClear={() => {
                    fetchData(1);
                    setLazyState({
                      first: 0,
                      rows,
                      page: 1,
                    });
                  }}
                />
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  if (loadData && total === undefined) return <Spinner />;
  return (
    <DataTable
      scrollable={scrollable}
      className={`OurDataTable-content${className ? ` ${className}` : ""}${headerTitle ? " limit" : ""}${!footer ? " bottomBorderRadius" : ""}`}
      header={filterByPeriod || onSearch ? renderHeader : undefined}
      value={data}
      stripedRows={stripedRows}
      tableStyle={tableStyle}
      paginator={data.length > 0 && data.length >= rows}
      paginatorTemplate="PrevPageLink CurrentPageReport NextPageLink"
      currentPageReportTemplate="{first}-{last} sur {totalRecords}"
      lazy={!!loadData}
      first={lazyState.first}
      rows={rows}
      totalRecords={loadData ? total : undefined}
      onPage={loadData ? onPage : undefined}
      loading={loadData ? loading : undefined}
      emptyMessage={<NoDataMessage />}
      headerColumnGroup={headerGroup}
      editMode={editMode}
      onRowEditComplete={onRowEditComplete}
      rowEditorInitIcon={<Icon name="pencil" color="#2866E3" width={17} height={17} />}
      rowEditorCancelIcon={
        <i className="pi pi-times" style={{ color: "black", fontSize: "1rem" }} />
      }
      rowEditorSaveIcon={<i className="pi pi-check" style={{ color: "black", fontSize: "1rem" }} />}
    >
      {columns.map((c) => (
        <Column
          key={c.key ?? c.field}
          field={c.field}
          header={c.header}
          body={c.body}
          style={c.style}
          editor={c.editor}
          headerStyle={c.headerStyle}
          bodyStyle={c.bodyStyle}
          rowEditor={c.rowEditor}
          colSpan={c.colSpan}
        />
      ))}
    </DataTable>
  );
}
